import { __decorate } from "tslib";
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { Mutation } from 'vuex-class';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import RouterParamsAppMixin from '@/helpers/vue_mixins/RouterParamsAppMixin';
import FormValidationsMixin from '@/helpers/vue_mixins/v2/FormValidationsMixin';
import FormMixin from '@/helpers/vue_mixins/FormMixin';
import AlertMixin from '@/helpers/vue_mixins/AlertMixin';
import WindowSizeListener from '@/helpers/vue_mixins/v3/WindowSizeListenerMixin';
import ActionButton from '@/components/action_button/Index.vue';
import RadioBoxGroup from '@/views/v3/RadioBoxGroup.vue';
import { requiredIf } from 'vuelidate/lib/validators';
import BaseTextField from '@/components/v2/BaseTextField.vue';
import NumericInput from '@/components/v2/NumericInput.vue';
import { requiredProperty } from '@/helpers/mixins/LocalizationHelper';
import SaveGlobalChatbotSettings from '@/plugins/graphql/mutations/chatbot_ai_settings/save-chatbot-global-settings.gql';
import ChatbotGlobalSettings from '@/plugins/graphql/queries/v2/chatbot_ai_settings/chatbot-global-settings.gql';
import moment from 'moment';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import SleepChatbot from '@/plugins/graphql/mutations/chatbot_ai_settings/sleep-chatbot.gql';
import chatbotGlobalSettingSubscriptionApp from '@/plugins/graphql/subscriptions/copilot_inbox_manager/chatbot-global-settings-app.gql';
let ChatbotSettings = class ChatbotSettings extends Mixins(ViewportSizeMixin, RouterParamsAppMixin, WindowSizeListener, FormValidationsMixin, Mixins(FormMixin, AlertMixin)) {
    constructor() {
        super(...arguments);
        this.options = [
            { value: 'Turn Off', subText: 'Turn off chatbot', index: 1 },
            { value: 'Copilot', subText: 'Helps you with recommended content', index: 2 },
            { value: 'Autopilot', subText: 'Will answer inquiry automatically', index: 3 },
        ];
        this.chatbotGlobalSetting = null;
        this.selectedOption = 2;
        this.asleep = false;
        this.saving = false;
        this.sleepUntil = null;
        this.channels = [
            {
                text: 'Facebook Messenger',
                value: 'messenger',
            },
            {
                text: 'Facebook',
                value: 'facebook_comment',
            },
        ];
        this.sleepOptions = [
            {
                text: 'All users',
                value: 'all_users',
                tooltipText: 'If a message is sent to any user manually, the bot will be put to sleep for all users.',
            },
            {
                text: 'Manually-messaged users only',
                value: 'manually_messaged_users',
                tooltipText: 'The bot will be put to sleep only for users who the merchant manually messages.',
            },
        ];
        this.timeLabels = ['Minutes', 'Hours'];
        this.minuteLabel = ['Minutes'];
        this.form = {
            operationMode: 'copilot',
            selectedChannels: [],
            waitTimeNumber: 5,
            waitTimeLabel: 'Minutes',
            maxMessage: '10',
            sleepTimeNumber: 1,
            sleepTimeLabel: 'Hours',
            applyTo: 'all_users',
        };
        this.delay = false;
        this.sleepAfterMessage = false;
        this.sleepCheckInterval = null;
        this.displaySleepMessage = false;
    }
    applySettings(settings) {
        let selectedOperation = settings.operationMode;
        if (selectedOperation === 'off') {
            selectedOperation = 'Turn Off';
        }
        const selectedIndex = this.options.find((option) => option.value.toLowerCase() === selectedOperation.toLowerCase())?.index;
        this.selectedOption = selectedIndex;
        const { channels } = settings;
        const selectedChannels = this.channels.filter((channel) => channels.includes(channel.value));
        this.form.selectedChannels = selectedChannels;
        this.sleepUntil = new Date(settings.sleepUntil);
        this.asleep = new Date(settings.sleepUntil) >= new Date();
        this.startSleepCheckTimer();
        if (selectedOperation !== 'off') {
            const delayStr = settings.delay;
            if (delayStr) {
                const delayArr = delayStr.split(' ');
                [this.form.waitTimeNumber, this.form.waitTimeLabel] = [Number(delayArr[0]), delayArr[1]];
            }
            else {
                this.form.waitTimeNumber = 5;
                this.form.waitTimeLabel = 'Minutes';
            }
            const sleepStr = settings.sleepDuration;
            if (sleepStr) {
                const sleepArr = sleepStr.split(' ');
                [this.form.sleepTimeNumber, this.form.sleepTimeLabel] = [Number(sleepArr[0]), sleepArr[1]];
            }
            else {
                this.form.sleepTimeNumber = 1;
                this.form.sleepTimeLabel = 'Hours';
            }
            this.form.applyTo = settings.sleepOption;
        }
    }
    get sleepDate() {
        const vm = this;
        const sleepUnitlDate = new Date(vm.chatbotGlobalSetting.sleepUntil);
        const now = new Date();
        if (sleepUnitlDate >= now) {
            return this.humanReadableDate(sleepUnitlDate);
        }
        const timeString = `${this.form.sleepTimeNumber} ${this.form.sleepTimeLabel}`; // Example input
        const sleepDurationMs = this.getSleepDurationInMs(timeString);
        const newSleepTime = new Date(now.getTime() + sleepDurationMs);
        return this.humanReadableDate(newSleepTime);
    }
    getSleepDurationInMs(timeString) {
        // Split the string into a number and a unit (e.g., "1 Hours" becomes ["1", "Hours"])
        const [amountStr, unit] = timeString.split(' ');
        const amount = parseInt(amountStr, 10);
        if (Number.isNaN(amount)) {
            throw new Error('Invalid time format');
        }
        // Convert based on the unit
        switch (unit.toLowerCase()) {
            case 'hours':
                return amount * 60 * 60 * 1000;
            case 'minutes':
                return amount * 60 * 1000;
            case 'seconds':
                return amount * 1000;
            default:
                throw new Error('Unsupported time unit');
        }
    }
    get sleepMin() {
        if (this.form.sleepTimeLabel === 'Minutes') {
            return '10';
        }
        return '1';
    }
    get sleepMax() {
        if (this.form.sleepTimeLabel === 'Minutes') {
            return '59';
        }
        return '24';
    }
    get optionsErrors() {
        const errors = [];
        // Ensure the validation object is dirty (i.e., has been interacted with)
        if (!this.$v.form.selectedChannels?.$dirty) {
            return errors;
        }
        // Check if the required validation has failed
        if (!this.$v.form.selectedChannels?.required) {
            const msg = requiredProperty('Channels');
            errors.push(msg);
        }
        return errors;
    }
    get delayErrors() {
        const errors = [];
        // Ensure the validation object is dirty (i.e., has been interacted with)
        if (!this.$v.form.waitTimeNumber?.$dirty) {
            return errors;
        }
        // Check if the required validation has failed
        if (!this.$v.form.waitTimeNumber?.required) {
            const msg = requiredProperty('Delay');
            errors.push(msg);
        }
        if (!this.$v.form.waitTimeNumber.invalidDelay) {
            const msg = 'Please select between 1 and 120';
            errors.push(msg);
        }
        return errors;
    }
    withinDelayValues(obj) {
        const numericObj = Number(obj);
        if ((numericObj >= 1 && numericObj <= 120) ||
            this.selectedOption === 2 ||
            this.selectedOption === 1) {
            return true;
        }
        return false;
    }
    get sleepErrors() {
        const errors = [];
        // Ensure the validation object is dirty (i.e., has been interacted with)
        if (!this.$v.form.sleepTimeNumber?.$dirty) {
            return errors;
        }
        // Check if the required validation has failed
        if (!this.$v.form.sleepTimeNumber?.required) {
            const msg = requiredProperty('Delay');
            errors.push(msg);
        }
        if (!this.$v.form.sleepTimeNumber.invalidSleep) {
            let msg = '';
            if (this.form.sleepTimeLabel === 'Minutes') {
                msg = 'Please select between 10 and 59';
            }
            if (this.form.sleepTimeLabel === 'Hours') {
                msg = 'Please select between 1 and 24';
            }
            errors.push(msg);
        }
        return errors;
    }
    withinSleepValues(obj) {
        const numericObj = Number(obj);
        if ((this.form.sleepTimeLabel === 'Minutes' && numericObj >= 10 && numericObj <= 59) ||
            this.selectedOption === 1) {
            return true;
        }
        if ((this.form.sleepTimeLabel === 'Hours' && numericObj >= 1 && numericObj <= 24) ||
            this.selectedOption === 1) {
            return true;
        }
        return false;
    }
    getOptionByIndex(index) {
        return this.options.find((option) => option.index === index);
    }
    humanReadableDate(date) {
        return moment(date).format('MMM DD YYYY h:mm A');
    }
    onSleeptimerChange() {
        const vm = this;
        vm.$v.form.sleepTimeNumber.$touch();
    }
    onDelaytimerChange() {
        const vm = this;
        vm.$v.form.waitTimeNumber.$touch();
    }
    save() {
        const vm = this;
        vm.$v.$touch();
        if (!vm.$v.$invalid) {
            vm.saving = true;
            const operationValue = vm.getOptionByIndex(vm.selectedOption).value;
            let operationMode = '';
            if (operationValue === 'Turn Off') {
                operationMode = 'off';
            }
            else {
                operationMode = operationValue.toLowerCase();
            }
            const delay = `${vm.form.waitTimeNumber} ${vm.form.waitTimeLabel}`;
            const sleepDuration = `${vm.form.sleepTimeNumber} ${vm.form.sleepTimeLabel}`;
            const channels = vm.form.selectedChannels.map((channel) => {
                return channel.value;
            });
            const sleepOption = vm.form.applyTo;
            const sleep = this.asleep;
            const variables = {
                input: {
                    appUuid: vm.appUUID,
                    operationMode,
                    channels,
                    delay,
                    sleep,
                    sleepDuration,
                    sleepOption,
                },
            };
            return vm.$apollo
                .mutate({
                mutation: SaveGlobalChatbotSettings,
                variables,
            })
                .then(() => {
                this.successNotification(`Successfully Saved Chatbot Settings`);
                vm.saving = false;
            })
                .catch((e) => {
                vm.saving = false;
                vm.errorNotification(e.message);
            })
                .finally(() => {
                this.$apollo.queries.chatbotGlobalSetting.refetch();
            });
        }
    }
    wakeUpChatbot() {
        const vm = this;
        this.asleep = false;
        const variables = {
            input: {
                appUuid: vm.appUUID,
            },
        };
        return vm.$apollo
            .mutate({
            mutation: SleepChatbot,
            variables,
        })
            .then(() => {
            this.successNotification(`Successfully Turned on Chatbot`);
            this.hideConfirmationDialog();
        })
            .catch((e) => {
            vm.errorNotification(e.message);
        })
            .finally(() => {
            this.$apollo.queries.chatbotGlobalSetting.refetch();
        });
    }
    sleepChatbot() {
        this.asleep = true;
    }
    sleepChecker() {
        if (!(this.sleepUntil >= new Date())) {
            this.stopSleepCheckTimer();
            this.displaySleepMessage = false;
        }
        else {
            this.displaySleepMessage = true;
        }
    }
    startSleepCheckTimer() {
        this.stopSleepCheckTimer();
        this.sleepChecker();
        if (this.sleepCheckInterval) {
            clearInterval(this.sleepCheckInterval);
        }
        this.sleepCheckInterval = setInterval(() => {
            this.sleepChecker();
        }, 1000); // Check every minute --- 1000 if every sec --- 60000 every minute
    }
    // Method to clear the timer
    stopSleepCheckTimer() {
        if (this.sleepCheckInterval) {
            clearInterval(this.sleepCheckInterval);
            this.sleepCheckInterval = null;
        }
    }
    // Clean up the timer when the component is destroyed
    beforeDestroy() {
        this.stopSleepCheckTimer();
    }
    mounted() {
        this.$apollo.queries.chatbotGlobalSetting.refetch();
    }
};
__decorate([
    Mutation('showConfirmationDialog')
], ChatbotSettings.prototype, "showConfirmationDialog", void 0);
__decorate([
    Mutation('hideConfirmationDialog')
], ChatbotSettings.prototype, "hideConfirmationDialog", void 0);
__decorate([
    Watch('form.sleepTimeNumber')
], ChatbotSettings.prototype, "onSleeptimerChange", null);
__decorate([
    Watch('form.waitTimeNumber')
], ChatbotSettings.prototype, "onDelaytimerChange", null);
ChatbotSettings = __decorate([
    Component({
        components: {
            ActionButton,
            RadioBoxGroup,
            BaseTextField,
            NumericInput,
            ConfirmationDialog,
        },
        apollo: {
            chatbotGlobalSetting: {
                query: ChatbotGlobalSettings,
                variables() {
                    return {
                        appUuid: this.$route.params.appUUID,
                    };
                },
                update(data) {
                    const settings = data.chatbotGlobalSettings;
                    this.applySettings(settings);
                    return data.chatbotGlobalSettings;
                },
                skip() {
                    return !this.$route.params.appUUID;
                },
                subscribeToMore: {
                    document: chatbotGlobalSettingSubscriptionApp,
                    variables() {
                        return {
                            appGlobalId: this.$store.state.appGlobalId,
                        };
                    },
                    updateQuery(previousResult, { subscriptionData }) {
                        const global_settings = subscriptionData.data.chatbotGlobalSettingsApp.chatbotGlobalSettings;
                        this.applySettings(global_settings);
                        return global_settings;
                    },
                },
            },
        },
        validations() {
            const vm = this;
            const autopilot = () => vm.selectedOption === 3;
            const copilot = () => vm.selectedOption === 2;
            const validations = {
                form: {
                    selectedChannels: {
                        required: requiredIf(autopilot || copilot),
                    },
                    waitTimeNumber: {
                        required: requiredIf(autopilot),
                        invalidDelay() {
                            const { waitTimeNumber } = this.form;
                            return vm.withinDelayValues(waitTimeNumber);
                        },
                    },
                    waitTimeLabel: {
                        required: requiredIf(autopilot),
                    },
                    sleepTimeNumber: {
                        required: requiredIf(autopilot || copilot),
                        invalidSleep() {
                            const { sleepTimeNumber } = this.form;
                            return vm.withinSleepValues(sleepTimeNumber);
                        },
                    },
                    sleepTimeLabel: {
                        required: requiredIf(autopilot || copilot),
                    },
                    applyTo: {
                        required: requiredIf(autopilot),
                    },
                },
            };
            return validations;
        },
    })
], ChatbotSettings);
export default ChatbotSettings;
